// @ts-nocheck
import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoleAssignments } from "@/hooks/useUserRoleAssignments";
import { useNavigate } from "react-router-dom";
import { 
  HelpCircle, 
  Settings, 
  LayoutDashboard, 
  UserCog, 
  Users, 
  CreditCard, 
  LogOut,
  ChevronRight,
  Menu,
  Key
} from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface ScoutNavbarProps {
  toggleSidebar?: () => void;
}

const ScoutNavbar: React.FC<ScoutNavbarProps> = ({ toggleSidebar }) => {
  const { profile, signOut } = useAuth();
  const { allowedDashboardPaths, isLoading } = useUserRoleAssignments();
  const navigate = useNavigate();

  // Create dashboard menu items based on allowed paths
  const getDashboardMenuItems = () => {
    const dashboardItems = [];
    
    if (allowedDashboardPaths.includes('/scout-dashboard')) {
      dashboardItems.push({
        label: 'Scout Dashboard',
        path: '/scout-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/athlete-dashboard')) {
      dashboardItems.push({
        label: 'Athlete Dashboard',
        path: '/athlete-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/parent-dashboard')) {
      dashboardItems.push({
        label: 'Parent Dashboard',
        path: '/parent-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/press-dashboard')) {
      dashboardItems.push({
        label: 'Press Dashboard',
        path: '/press-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/admin-dashboard')) {
      dashboardItems.push({
        label: 'Admin Dashboard',
        path: '/admin-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/college-coach-dashboard')) {
      dashboardItems.push({
        label: 'College Coach Dashboard',
        path: '/college-coach-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/club-coach-dashboard')) {
      dashboardItems.push({
        label: 'Club Coach Dashboard',
        path: '/club-coach-dashboard'
      });
    }
    
    if (allowedDashboardPaths.includes('/vendor-press-dashboard')) {
      dashboardItems.push({
        label: 'Vendor Dashboard',
        path: '/vendor-press-dashboard'
      });
    }
    
    return dashboardItems;
  };

  const dashboardItems = getDashboardMenuItems();
  const showDashboardSection = !isLoading && dashboardItems.length > 0;

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const handleResetPassword = async () => {
    if (!profile?.email) {
      toast.error('Email not found');
      return;
    }
    
    try {
      const { error } = await invokeEdgeFunction('send-password-reset', {
        body: { email: profile.email }
      });
      
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Failed to send password reset email');
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-3 md:px-4 py-3 sticky top-0 z-30">
      <div className="flex items-center justify-between">
        {/* Mobile menu button */}
        {toggleSidebar && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleSidebar}
            className="md:hidden p-2 min-h-[44px] min-w-[44px]"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Open sidebar</span>
          </Button>
        )}
        
        {/* Title - responsive sizing */}
        <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate mx-2">
          <span className="hidden sm:inline">Scout Dashboard</span>
          <span className="sm:hidden">Scout</span>
        </h1>
        
        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-10 w-10 md:h-8 md:w-8 p-0 touch-manipulation">
              <Avatar className="h-8 w-8 md:h-8 md:w-8">
                <AvatarImage src={profile?.avatar_url} alt={profile?.name} />
                <AvatarFallback className="text-sm">
                  {profile?.name?.split(' ').map(n => n[0]).join('') || 'S'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-56 z-50 bg-white border border-gray-200 shadow-lg"
            sideOffset={5}
          >
            <DropdownMenuLabel className="truncate">{profile?.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => navigate('/scout-dashboard/profile')}
              className="cursor-pointer min-h-[44px] md:min-h-0"
            >
              <UserCog className="mr-2 h-4 w-4 flex-shrink-0" />
              Profile
            </DropdownMenuItem>
            
            {showDashboardSection && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="min-h-[44px] md:min-h-0">
                    <LayoutDashboard className="mr-2 h-4 w-4 flex-shrink-0" />
                    My Dashboards
                    <ChevronRight className="ml-auto h-4 w-4 flex-shrink-0" />
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="z-50 bg-white border border-gray-200 shadow-lg">
                    {dashboardItems.map((item, index) => (
                      <DropdownMenuItem 
                        key={`${item.path}-${index}`}
                        onClick={() => navigate(item.path)}
                        className="cursor-pointer min-h-[44px] md:min-h-0"
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}
            
            <DropdownMenuItem 
              onClick={() => navigate('/scout-dashboard/athletes')}
              className="cursor-pointer min-h-[44px] md:min-h-0"
            >
              <Users className="mr-2 h-4 w-4 flex-shrink-0" />
              My Athletes
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              onClick={() => navigate('/scout-dashboard/settings')}
              className="cursor-pointer min-h-[44px] md:min-h-0"
            >
              <Settings className="mr-2 h-4 w-4 flex-shrink-0" />
              Settings
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              onClick={() => navigate("/help")}
              className="cursor-pointer min-h-[44px] md:min-h-0"
            >
              <HelpCircle className="mr-2 h-4 w-4 flex-shrink-0" />
              Help
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={handleResetPassword} 
              className="cursor-pointer min-h-[44px] md:min-h-0"
            >
              <Key className="mr-2 h-4 w-4 flex-shrink-0" />
              Reset Password
            </DropdownMenuItem>
            
            <DropdownMenuItem 
              onClick={handleLogout} 
              className="cursor-pointer text-red-600 min-h-[44px] md:min-h-0"
            >
              <LogOut className="mr-2 h-4 w-4 flex-shrink-0" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default ScoutNavbar;